import {Injectable} from '@angular/core';
import {Observable} from 'rxjs/internal/Observable';
import {HttpClient} from '@angular/common/http';
import {BaseService} from '../base/base.service';
import {catchError, map, tap} from 'rxjs/operators';
import {ConfigService} from "../base/config.service";
import {AppConfig} from "../base/app-config";
import {Customer, Facet, StockItem, StockItemHistory, StockItemSearchResult} from "./feature-print-models";
import {Identification, IdentificationSearchResult} from "./identification-models";
import {of} from "rxjs";

const PORTAL_USER_ROLE = 'PORTAL-USER';
const TRACE_USER_ROLE = 'TRACE-VIEW';
const SUPERUSER_ROLE = 'SUPERUSER';

export class Page {
  pageNumber: number;
  pageSize: number;

  constructor(pageNumber: number, pageSize: number) {
    this.pageNumber = pageNumber;
    this.pageSize = pageSize;
  }
}

export class SearchCriteria {
  dataset: string | null;
  startDate: Date;
  endDate: Date;
  searchText: string | null;
  page: Page | null;

  constructor(
    dataset: string,
    startDate: Date,
    endDate: Date,
    searchText: string | null = null,
    page: Page | null = null
  ) {
    this.startDate = startDate;
    this.endDate = endDate;
    this.dataset = dataset;
    this.searchText = searchText;
    this.page = page;
  }
}

export class Counts {
  count: number;

  constructor(counts: number) {
    this.count = counts;
  }
}

export class CountsByDay {
  day: string;
  count: number;

  constructor(date: string, count: number) {
    this.day = date;
    this.count = count;
  }
}

export interface Download {
  downloadUrl: string;
}

@Injectable()
export class PortalApiService {

  constructor(private baseSvc: BaseService, private configSvc: ConfigService<AppConfig>, private http: HttpClient) {
  }

  public dayInMillis = (1000 * 60 * 60 * 24);

  public computePreviewUrl(previewKey: string, dataset: string): string {
    return this.baseSvc.gatewayUrl(`v1/${this.baseSvc.appConfig().customerName}/${dataset}/${PORTAL_USER_ROLE}/generate-download-url?assetType=preview&assetKey=${previewKey}`);
  }

  public getTraceDownloadUrl(assetKey: string, dataset: string, stockItemId: string): Observable<Download> {
    const url = this.baseSvc.gatewayUrl(`v2/${this.baseSvc.appConfig().customerName}/storage-service/${TRACE_USER_ROLE}/${dataset}/stock-item/${stockItemId}/trace-digital-asset-download-url?assetCategory=trace&assetKey=${assetKey}`);
    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        map((download: any) => download as string)
      );

    return req;
  }

  public getPreviewUrl(previewKey: string, dataset: string): Observable<string> {
    const url = this.baseSvc.gatewayUrl(`v1/${this.baseSvc.appConfig().customerName}/${dataset}/${PORTAL_USER_ROLE}/generate-download-url?assetType=preview&assetKey=${previewKey}`);
    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        map((url: any) => url as string)
      );

    return req;
  }

  public getStockItem(stockItemId: string, dataset: string): Observable<StockItem> {

    const url = this.baseSvc.customerUrl(`${dataset}/${PORTAL_USER_ROLE}/stock-item-view/${stockItemId}`);
    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        map((stockItem: any) => stockItem as StockItem)
      );

    return req;
  }

  public getFacets(stockItemId: string, dataset: string): Observable<Facet[]> {

    const url = this.baseSvc.customerUrl(`${dataset}/${PORTAL_USER_ROLE}/stock-item-view/${stockItemId}/facets`);
    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        map((facets: any) => facets as Facet[])
      );

    return req;
  }

  public getStockItemHistory(stockItemId: string, dataset: string): Observable<StockItemHistory[]> {

    const url = this.baseSvc.customerUrl(`${dataset}/${TRACE_USER_ROLE}/stock-item-history-view/${stockItemId}`);
    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        map((history: any) => history as StockItemHistory[])
      );

    return req;
  }

  public searchStockItems(search: SearchCriteria): Observable<StockItemSearchResult[]> {

    const url = this.baseSvc.customerUrl(`${search.dataset}/${PORTAL_USER_ROLE}/featureprint-view/search`);
    let searchParams: any = {};
    searchParams.start = search.startDate.toISOString();
    searchParams.end = search.endDate.toISOString();
    searchParams.dataset = search.dataset;

    if (search.page) {
      searchParams['offset'] = search.page.pageNumber * search.page.pageSize;
      searchParams['limit'] = search.page.pageSize;
    }
    if (search.searchText && search.searchText.length > 0) {
      searchParams.searchText = search.searchText;
    }

    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions({params: searchParams}))
      .pipe(
        map((featurePrints: any) => featurePrints as StockItemSearchResult[])
      );

    return req;
  }

  public featurePrintSearchCount(search: SearchCriteria): Observable<number> {
    const url = this.baseSvc.customerUrl(`${search.dataset}/${PORTAL_USER_ROLE}/featureprint-view/search/count`);
    let searchParams: any = {};
    searchParams.start = search.startDate.toISOString();
    searchParams.end = search.endDate.toISOString();
    searchParams.dataset = search.dataset;

    if (search.searchText && search.searchText.length > 0) {
      searchParams.searchText = search.searchText;
    }

    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions({params: searchParams}))
      .pipe(
        map((total: any) => total as number)
      );

    return req;
  }

  public getIdentification(identificationId: string, dataset: string): Observable<Identification> {

    const url = this.baseSvc.customerUrl(`${dataset}/${PORTAL_USER_ROLE}/identification-view/${identificationId}`);
    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        map((identification: any) => identification as Identification)
      );

    return req;
  }

  public searchIdentifications(search: SearchCriteria): Observable<IdentificationSearchResult[]> {

    const url = this.baseSvc.customerUrl(`${search.dataset}/${PORTAL_USER_ROLE}/identification-view/search`);
    let searchParams: any = {};
    searchParams.start = search.startDate.toISOString();
    searchParams.end = search.endDate.toISOString();
    searchParams.dataset = search.dataset;

    if (search.page) {
      searchParams['offset'] = search.page.pageNumber * search.page.pageSize;
      searchParams['limit'] = search.page.pageSize;
    }
    if (search.searchText && search.searchText.trim() !== '') {
      searchParams.searchText = search.searchText;
    }

    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions({params: searchParams}))
      .pipe(
        map((results: any) => results as IdentificationSearchResult[])
      );

    return req;
  }

  public identificationSearchCount(search: SearchCriteria): Observable<number> {
    const url = this.baseSvc.customerUrl(`${search.dataset}/${PORTAL_USER_ROLE}/identification-view/search/count`);
    let searchParams: any = {};
    searchParams.start = search.startDate.toISOString();
    searchParams.end = search.endDate.toISOString();
    searchParams.dataset = search.dataset;

    if (search.searchText && search.searchText.length > 0) {
      searchParams.searchText = search.searchText;
    }

    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions({params: searchParams}))
      .pipe(
        map((count: any) => count as number)
      );

    return req;
  }

  public facetCountsByDay(dataset: string, start: Date, end: Date): Observable<CountsByDay[]> {
    const url = this.baseSvc.customerUrl(`${dataset}/${PORTAL_USER_ROLE}/featureprints-count/total-by-day`);
    let searchParams: any = {};
    searchParams.start = start.toISOString();
    searchParams.end = end.toISOString();

    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions({params: searchParams}))
      .pipe(
        catchError((error) => of([])),
        map((results: any) => results as CountsByDay[])
      );

    return req;
  }

  public identificationCountsByDay(dataset: string, start: Date, end: Date): Observable<CountsByDay[]> {
    const url = this.baseSvc.customerUrl(`${dataset}/${PORTAL_USER_ROLE}/identifications-count/total-by-day`);
    let searchParams: any = {};
    searchParams.start = start.toISOString();
    searchParams.end = end.toISOString();

    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions({params: searchParams}))
      .pipe(
        catchError((error) => of([])),
        map((results: any) => results as CountsByDay[])
      );

    return req;
  }

  public activeStockItemCount(dataset: string): Observable<Counts> {
    const url = this.baseSvc.customerUrl(`${dataset}/${PORTAL_USER_ROLE}/featureprints-count/total/active`);

    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        catchError((error) => of({count: -1})),
        map((total: any) => total as Counts)
      );

    return req;
  }

  public totalStockItemCount(dataset: string): Observable<Counts> {
    const url = this.baseSvc.customerUrl(`${dataset}/${PORTAL_USER_ROLE}/featureprints-count/total`);

    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        catchError((error) => of({count: -1})),
        map((total: any) => total as Counts)
      );

    return req;
  }

  public totalFacetCount(dataset: string): Observable<Counts> {
    const url = this.baseSvc.customerUrl(`${dataset}/${PORTAL_USER_ROLE}/featureprints-count/total/facet`);

    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        catchError((error) => of({count: -1})),
        map((total: any) => total as Counts)
      );

    return req;
  }

  public totalIdentificationCount(dataset: string): Observable<Counts> {
    const url = this.baseSvc.customerUrl(`${dataset}/${PORTAL_USER_ROLE}/identifications-count/total`);

    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        catchError((error) => of({count: -1})),
        map((total: any) => total as Counts)
      );

    return req;
  }

  public getCustomerList(): Observable<Customer[]> {
    const customerName = "alitheon"; // fixme when the backend fixes this remove this !!!!
    const url = this.baseSvc.gatewayUrl(`records-service/v1/${customerName}/${SUPERUSER_ROLE}/list-all`);
    const req: Observable<any> = this.http
      .get(url, this.baseSvc.defaultOptions())
      .pipe(
        map((list: any) => list.result as Customer[])
      );
    return req;
  }

}
